import {
  CLUB_STYLES,
  CLUBS_PER_DIVISION,
  DIVISIONS,
  type Club,
  type ClubStyle,
} from "./model.js";
import { clubKitPool } from "./kits.js";
import { CLUB_NAMES, COACH_SURNAMES, FIXED_CLUBS, QUIRKS, STADIUMS, STAR_NAME_SPACE, starName, type StadiumSeed } from "./names.js";
import { between, rngFor, shuffle } from "./rng.js";

/**
 * Rango de fuerza de cada division. Se solapan un poco a proposito: el mejor
 * de Regional puede con el peor de Segunda, y por eso un ascenso no es un
 * paseo ni un descenso una condena.
 */
/**
 * Version de las listas de nombres, chistes y estadios (`names.ts`) y de como
 * se reparten. La carrera viaja a la cuenta SIN los clubes (se regeneran de la
 * semilla, ver `cloud.ts`), asi que cambiar una lista cambiaria los clubes de
 * las carreras ya empezadas en otro aparato. Si se toca, se sube esto: una
 * copia de otra version no se regenera, se ignora. Lo vigila un test.
 */
export const WORLD_VERSION = 1;

export const TIER_RATING: readonly (readonly [number, number])[] = [
  [32, 50],
  [46, 62],
  [58, 74],
  [70, 88],
];

/**
 * Cuanta gente va al campo en cada division (de menos a mas fuerte). En el
 * barrio, cuatro padres y un perro; en Primera, lleno.
 */
const TIER_CROWD: readonly (readonly [number, number])[] = [
  [0.12, 0.3],
  [0.28, 0.5],
  [0.5, 0.78],
  [0.8, 1],
];

/** Lo que aguanta una fuerza de club, pase lo que pase. */
export const CLUB_RATING_MIN = 25;
export const CLUB_RATING_MAX = 94;

/**
 * El mundo entero a partir de una semilla: 4 divisiones de 8 clubes.
 *
 * Los clubes fijos (`FIXED_CLUBS`: Ytuano FC, Malaka FC) estan en todos los
 * mundos, en su division, con su camiseta y su fama. El resto se sortea.
 *
 * Con que club empiezas NO lo decide el mundo: lo elige el jugador entre los
 * de la Liga del Barrio al crear la carrera.
 */
export function createWorld(seed: number): Club[] {
  const clubs: Club[] = [];
  const stars = shuffle(
    rngFor(seed, "stars"),
    Array.from({ length: STAR_NAME_SPACE }, (_, i) => i),
  );
  let starCursor = 0;
  // Una camiseta DISTINTA por club en todo el mundo: cada club se reconoce de
  // un vistazo, y acabar una temporada en cualquiera te da una nueva.
  const kits = shuffle(rngFor(seed, "kits"), clubKitPool());
  let kitCursor = 0;

  for (const division of DIVISIONS) {
    const tier = division.tier;
    const random = rngFor(seed, "world", tier);
    const fixed = FIXED_CLUBS.filter((f) => f.tier === tier);
    const fixedNames = new Set(fixed.map((f) => f.name));

    const names = shuffle(
      random,
      CLUB_NAMES[tier]!.filter((n) => !fixedNames.has(n)),
    ).slice(0, CLUBS_PER_DIVISION - fixed.length);
    const coaches = shuffle(random, COACH_SURNAMES).slice(0, CLUBS_PER_DIVISION);
    const quirks = shuffle(random, QUIRKS).slice(0, CLUBS_PER_DIVISION);
    // Todos los estilos aparecen al menos una vez en cada division: una liga
    // con cinco equipos que juegan igual no tiene personalidad.
    const styles: ClubStyle[] = shuffle(random, [
      ...CLUB_STYLES,
      ...Array.from({ length: CLUBS_PER_DIVISION - CLUB_STYLES.length }, () =>
        CLUB_STYLES[between(random, 0, CLUB_STYLES.length - 1)]!,
      ),
    ]);
    const [lo, hi] = TIER_RATING[tier]!;
    // Fuerzas repartidas por el rango (hay un gallito y hay un farolillo), con
    // algo de ruido. Los fijos se quedan con el puesto que tienen asignado; el
    // resto se baraja.
    const ladder = Array.from({ length: CLUBS_PER_DIVISION }, (_, i) =>
      Math.round(lo + ((hi - lo) * i) / (CLUBS_PER_DIVISION - 1) + between(random, -2, 2)),
    );
    const reserved = new Set(fixed.map((f) => f.strengthRank));
    const freeRatings = shuffle(
      random,
      ladder.filter((_, i) => !reserved.has(i)),
    );
    const stadiums = shuffle(random, STADIUMS[tier]!);
    const [crowdLo, crowdHi] = TIER_CROWD[tier]!;

    const entries: Array<{ name: string; rating: number; kitId: string; quirk: string; stadium: StadiumSeed }> = [
      ...fixed.map((f) => ({
        name: f.name,
        rating: ladder[f.strengthRank]!,
        kitId: f.kitId,
        quirk: f.quirk,
        stadium: f.stadium,
      })),
      ...names.map((name, i) => ({
        name,
        rating: freeRatings[i]!,
        kitId: kits[kitCursor++ % kits.length]!,
        quirk: quirks[i]!,
        stadium: stadiums[i]!,
      })),
    ];

    entries.forEach((entry, i) => {
      const starIndex = stars[starCursor++]!;
      // El mas fuerte de la division llena mas que el farolillo.
      const strength = Math.max(0, Math.min(1, (entry.rating - lo) / Math.max(1, hi - lo)));
      clubs.push({
        id: `c${tier}${i}`,
        name: entry.name,
        tier,
        rating: entry.rating,
        style: styles[i]!,
        kitId: entry.kitId,
        coach: coaches[i]!,
        star: starName(starIndex, Math.floor(starIndex / 16)),
        quirk: entry.quirk,
        stadium: {
          name: entry.stadium.name,
          skinId: entry.stadium.skin,
          crowd: Math.round((crowdLo + (crowdHi - crowdLo) * strength) * 100) / 100,
        },
      });
    });
  }

  return clubs;
}

export function clubById(clubs: readonly Club[], id: string): Club {
  const club = clubs.find((c) => c.id === id);
  if (!club) throw new Error(`Club desconocido: ${id}`);
  return club;
}

export function clubsInTier(clubs: readonly Club[], tier: number): Club[] {
  return clubs.filter((c) => c.tier === tier);
}
